import type { EChartsOption } from 'echarts/types/dist/shared';

import type { ReportsChartOptions, ReportsChartTheme } from '@core/reports-types';
import type { Theme } from '@core/theme-util';

export const getChartColors = (colors: Theme): EChartsOption['color'] & string[] => [
  colors.colorChartPrimaryDefault,
  colors.colorChartPurpleDefault,
  colors.colorChartGreenDefault,
  colors.colorChartYellowDefault,
  colors.colorChartYellowSubduedDefault,
  colors.colorChartPurpleSubduedDefault,
  colors.colorChartBlueStrongDefault,
  colors.colorChartGreenSubduedDefault,
  colors.colorChartOrangeDefault,
  colors.colorChartTurquoiseDefault,
  colors.colorChartBlueDefault,
  colors.colorChartGrayDefault,
];

export const getChartTheme = (theme: Theme): ReportsChartTheme => ({
  colors: getChartColors(theme),
  textColor: theme.colorTextStrongDefault,
  splitLinesColor: theme.colorBorderLowDefault,
  ticksColor: theme.colorBorderLowDefault,
  pieChartLabelValue: theme.colorTextDefault,
  labelBorder: theme.colorBorderOncomponentDefault,
  sliderBgColor: theme.colorBgDefault,
  sliderColor: theme.colorBgNeutralStrongDefault,
  sliderBorder: theme.colorBorderDefault,
  pageIconColor: theme.colorIconDefault,
  pageIconDisabledColor: theme.colorIconDisabled,
  pageTextColor: theme.colorTextDefault,
});

/**
 * returns echart options that should be used with Echarts.init as theme param
 */

export const getChartThemeInitOptions = (theme: Theme): EChartsOption => {
  const chartTheme = getChartTheme(theme);
  return {
    textStyle: {
      color: chartTheme.textColor,
      // color: Colors.colorDarkest,
      fontFamily:
        "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Helvetica, Arial, sans-serif",
      fontWeight: 500,
      fontSize: 13,
    },
    title: {
      top: 'bottom',
    },
    label: {
      fontSize: 12,
      fontWeight: 600,
      color: chartTheme.textColor,
      textBorderColor: chartTheme.labelBorder,
      textBorderWidth: 2,
      rich: {
        pct: { fontSize: 12 },
      },
    },
    legend: {
      top: 10,
      right: 20,
      left: 20,
      itemWidth: 15,
      itemHeight: 10,
      textStyle: {
        fontSize: 11,
        color: chartTheme.textColor,
      },
    },
    axisLabel: {
      fontWeight: 600,
      color: chartTheme.textColor,
    },
    line: {
      smooth: 0.3,
    },
    darkMode: true,
    color: chartTheme.colors,
  };
};

type Unpacked<T> = T extends Array<infer U> ? U : T;
const styleGraphicTexts = (
  chartTheme: ReportsChartTheme,
  graphic: ReportsChartOptions['graphic'],
): ReportsChartOptions['graphic'] => {
  if (!graphic) {
    return graphic;
  }
  const mapFn = (gr: Unpacked<NonNullable<ReportsChartOptions['graphic']>>) => {
    if (gr.type === 'text') {
      return <any>{
        ...gr,
        style: {
          ...('style' in gr ? gr['style'] : {}),
          fill: chartTheme.textColor,
          font: "600 13px 'Segoe UI', Roboto, 'Helvetica Neue', Helvetica, Arial, sans-serif",
        },
      };
    }

    if ('elements' in gr && gr['elements']) {
      return {
        ...gr,
        elements: gr.elements.map(x => styleGraphicTexts(chartTheme, x)),
      };
    }

    return {
      ...gr,
      ...('children' in gr &&
        gr['children'] && {
          children: gr.children.map(x => styleGraphicTexts(chartTheme, x)),
        }),
    };
  };

  if (!Array.isArray(graphic)) {
    return mapFn(graphic);
  }

  return graphic.map(mapFn);
};

const styleDataZoom = (
  chartTheme: ReportsChartTheme,
  dataZoom: EChartsOption['dataZoom'],
): ReportsChartOptions['dataZoom'] => {
  if (!dataZoom) {
    return dataZoom;
  }
  const dz = Array.isArray(dataZoom) ? dataZoom : [dataZoom];

  return dz.map(dz => {
    if (dz.type !== 'slider') {
      return dz;
    }
    return {
      ...dz,
      backgroundColor: chartTheme.sliderBgColor,
      fillerColor: chartTheme.sliderColor,
      borderColor: chartTheme.sliderBorder,
    };
  });
};

/**
 * returns echart options that should be merged in NormalMode to achieve proper color theme
 */

export const getChartThemeStylingOptions = (
  options: EChartsOption,
  theme: Theme,
): EChartsOption => {
  const chartTheme = getChartTheme(theme);
  const graphic = styleGraphicTexts(chartTheme, options.graphic);
  const dataZoom = styleDataZoom(
    chartTheme,
    Array.isArray(options.dataZoom) ? options.dataZoom[0] : options.dataZoom,
  );
  return {
    yAxis: {
      axisLabel: {
        color: chartTheme.textColor,
      },
      splitLine: {
        lineStyle: {
          color: chartTheme.splitLinesColor,
        },
      },
    },
    xAxis: {
      axisLabel: {
        color: chartTheme.textColor,
      },
      splitLine: {
        lineStyle: {
          color: chartTheme.splitLinesColor,
        },
      },
    },
    legend: {
      pageIconColor: chartTheme.pageIconColor,
      pageIconInactiveColor: chartTheme.pageIconDisabledColor,
      pageTextStyle: {
        color: chartTheme.pageTextColor,
      },
    },
    graphic,
    dataZoom,
  };
};
